import {
  createGame,
  getCurrentAlert,
  submitDecision,
  getProgress,
  getTimeRemaining,
  getGrade,
} from '../../lib/alert-triage/engine';
import type { Action, Alert, Difficulty, GameState } from '../../lib/alert-triage/types';
import {
  ACTION_LABELS,
  SEVERITY_COLORS,
  DIFFICULTY_CONFIG,
} from '../../lib/alert-triage/types';

// DOM Elements
const startScreen = document.getElementById('triage-start-screen');
const gameScreen = document.getElementById('triage-game-screen');
const resultsScreen = document.getElementById('triage-results-screen');
const difficultyButtons =
  document.querySelectorAll<HTMLButtonElement>('[data-difficulty]');
const alertCard = document.getElementById('triage-alert-card');
const contextPanel = document.getElementById('triage-context-panel');
const contextToggle = document.getElementById('triage-context-toggle');
const actionButtons = document.querySelectorAll<HTMLButtonElement>('[data-action]');
const progressBar = document.getElementById('triage-progress-bar');
const progressText = document.getElementById('triage-progress-text');
const scoreDisplay = document.getElementById('triage-score');
const streakDisplay = document.getElementById('triage-streak');
const timerDisplay = document.getElementById('triage-timer');
const feedbackOverlay = document.getElementById('triage-feedback');
const feedbackIcon = document.getElementById('triage-feedback-icon');
const feedbackTitle = document.getElementById('triage-feedback-title');
const feedbackText = document.getElementById('triage-feedback-text');
const feedbackContinue = document.getElementById('triage-feedback-continue');
const resultsStats = document.getElementById('triage-results-stats');
const resultsReview = document.getElementById('triage-results-review');
const playAgainBtn = document.getElementById('triage-play-again');

if (
  !startScreen ||
  !gameScreen ||
  !resultsScreen ||
  !alertCard ||
  !contextPanel ||
  !contextToggle ||
  !progressBar ||
  !progressText ||
  !scoreDisplay ||
  !streakDisplay ||
  !feedbackOverlay ||
  !feedbackIcon ||
  !feedbackTitle ||
  !feedbackText ||
  !feedbackContinue ||
  !resultsStats ||
  !resultsReview ||
  !playAgainBtn
) {
  throw new Error('Alert Triage elements not found');
}

let gameState: GameState | null = null;
let alertStartTime = 0;
let timerInterval: ReturnType<typeof setInterval> | null = null;
let contextExpanded = false;
let actionsDisabled = false;

function setActionsEnabled(enabled: boolean) {
  actionsDisabled = !enabled;
  actionButtons.forEach((btn) => {
    btn.disabled = !enabled;
    btn.classList.toggle('opacity-60', !enabled);
    btn.classList.toggle('cursor-not-allowed', !enabled);
  });
}

function showScreen(screen: 'start' | 'game' | 'results') {
  startScreen.classList.toggle('hidden', screen !== 'start');
  gameScreen.classList.toggle('hidden', screen !== 'game');
  resultsScreen.classList.toggle('hidden', screen !== 'results');
}

function startGame(difficulty: Difficulty) {
  gameState = createGame(difficulty);
  alertStartTime = Date.now();
  setActionsEnabled(true);
  showScreen('game');
  renderCurrentAlert();
  startTimer();
  updateProgress();
  updateScore();
}

function startTimer() {
  if (timerInterval) clearInterval(timerInterval);
  if (!gameState || gameState.config.timeLimit === 0) {
    if (timerDisplay) timerDisplay.textContent = '--';
    return;
  }

  timerInterval = setInterval(() => {
    if (!gameState || !timerDisplay) return;
    const remaining = getTimeRemaining(gameState, alertStartTime);
    if (remaining === null) {
      timerDisplay.textContent = '--';
      return;
    }
    timerDisplay.textContent = `${Math.ceil(remaining)}s`;
    timerDisplay.className =
      remaining <= 5
        ? 'text-2xl font-semibold tabular-nums text-rose-500'
        : 'text-2xl font-semibold tabular-nums text-text';

    // Auto-submit as "investigate" if time runs out (safest default)
    if (remaining <= 0) {
      handleAction('investigate');
    }
  }, 100);
}

function stopTimer() {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
}

function renderCurrentAlert() {
  if (!gameState || !alertCard) return;

  const alert = getCurrentAlert(gameState);
  if (!alert) {
    endGame();
    return;
  }

  // Reset context panel
  contextExpanded = false;
  contextPanel?.classList.add('hidden');
  if (contextToggle) {
    contextToggle.textContent = 'Show Context ▼';
  }

  setActionsEnabled(true);

  // Render alert card
  const severityColor = SEVERITY_COLORS[alert.severity];
  const severityBg =
    alert.severity === 'CRITICAL'
      ? 'bg-rose-500/10 border-rose-500/30'
      : alert.severity === 'HIGH'
        ? 'bg-orange-500/10 border-orange-500/30'
        : alert.severity === 'MEDIUM'
          ? 'bg-amber-500/10 border-amber-500/30'
          : 'bg-emerald-500/10 border-emerald-500/30';

  // Attack chain indicator
  const chainBadge = alert.attackChainId
    ? `<span class="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-400 border border-purple-500/30">
        🔗 Chain ${alert.attackChainStep}/${alert.attackChainTotal}
       </span>`
    : '';

  alertCard.innerHTML = `
    <div class="rounded-lg border ${severityBg} p-4 mb-4">
      <div class="flex items-center justify-between gap-4 mb-3">
        <div class="flex items-center gap-2">
          <span class="text-xs uppercase tracking-wider ${severityColor} font-medium">${alert.severity}</span>
          ${chainBadge}
        </div>
        <span class="text-xs text-muted">${alert.timestamp}</span>
      </div>
      <h3 class="text-lg font-medium text-text mb-2">${alert.title}</h3>
      <p class="text-sm text-muted mb-4">${alert.summary}</p>
      <div class="grid gap-2 text-sm">
        ${Object.entries(alert.details)
          .map(
            ([key, value]) => `
          <div class="flex justify-between gap-4">
            <span class="text-muted">${key}:</span>
            <span class="text-text font-mono text-right">${Array.isArray(value) ? value.join(', ') : value}</span>
          </div>
        `
          )
          .join('')}
      </div>
      ${
        alert.mitreTechnique
          ? `
        <div class="mt-4 pt-3 border-t border-border/50">
          <span class="text-xs text-muted">MITRE ATT&CK: </span>
          <span class="text-xs text-accent">${alert.mitreTechnique}</span>
        </div>
      `
          : ''
      }
    </div>
  `;

  // Render context panel content
  renderContextPanel(alert);

  // Update alert queue pressure indicator
  renderQueuePressure();
}

function renderQueuePressure() {
  if (!gameState) return;

  const queueContainer = document.getElementById('triage-queue-pressure');
  if (!queueContainer) return;

  const remaining = gameState.alerts.length - gameState.currentIndex - 1;
  const maxPreview = 5;
  const previewCount = Math.min(remaining, maxPreview);

  if (remaining === 0) {
    queueContainer.innerHTML = '';
    return;
  }

  // Get upcoming alerts for preview
  const upcomingAlerts = gameState.alerts.slice(
    gameState.currentIndex + 1,
    gameState.currentIndex + 1 + previewCount
  );

  // Severity-based sizing and colors
  const getSeveritySize = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'w-3 h-3';
      case 'HIGH':
        return 'w-2.5 h-2.5';
      case 'MEDIUM':
        return 'w-2 h-2';
      default:
        return 'w-1.5 h-1.5';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'bg-rose-500';
      case 'HIGH':
        return 'bg-orange-500';
      case 'MEDIUM':
        return 'bg-amber-500';
      default:
        return 'bg-emerald-500';
    }
  };

  queueContainer.innerHTML = `
    <div class="flex items-center gap-2">
      <span class="text-xs text-muted">Queue:</span>
      <div class="flex items-center gap-1">
        ${upcomingAlerts
          .map(
            (a) => `
          <div class="rounded-full ${getSeveritySize(a.severity)} ${getSeverityColor(a.severity)} ${a.attackChainId ? 'ring-1 ring-purple-500' : ''}"
               title="${a.severity}${a.attackChainId ? ' (Chain)' : ''}"></div>
        `
          )
          .join('')}
        ${remaining > maxPreview ? `<span class="text-xs text-muted ml-1">+${remaining - maxPreview}</span>` : ''}
      </div>
      ${remaining > 10 ? '<span class="text-xs text-rose-400 animate-pulse ml-2">Alerts piling up!</span>' : ''}
    </div>
  `;
}

function renderContextPanel(alert: Alert) {
  if (!contextPanel) return;

  const { context } = alert;
  const {
    source,
    target,
    relatedAlerts,
    baselineNote,
    threatIntelNote,
    ticketReference,
    chainScope,
  } = context;

  contextPanel.innerHTML = `
    <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      ${
        chainScope
          ? `
        <div class="rounded-lg border border-purple-500/30 bg-purple-500/5 p-3 md:col-span-2 lg:col-span-4">
          <h4 class="text-xs uppercase tracking-wider text-purple-300 mb-2">Chain Scope</h4>
          <div class="grid gap-2 text-sm md:grid-cols-2">
            <div>
              <span class="text-muted">Source:</span>
              <span class="font-mono text-text ml-1">${chainScope.source.ip}</span>
              <span class="text-muted ml-2">(${chainScope.source.type})</span>
            </div>
            <div>
              <span class="text-muted">Target:</span>
              <span class="font-mono text-text ml-1">${chainScope.target.hostname}</span>
              <span class="text-muted ml-2">(${chainScope.target.role})</span>
            </div>
          </div>
        </div>
      `
          : ''
      }
      <!-- Source Info -->
      <div class="rounded-lg border border-border/50 bg-surface/50 p-3">
        <h4 class="text-xs uppercase tracking-wider text-muted mb-2">Source</h4>
        <div class="space-y-1 text-sm">
          <div><span class="text-muted">IP:</span> <span class="font-mono text-text">${source.ip}</span></div>
          <div><span class="text-muted">Type:</span> <span class="text-text">${source.type}</span></div>
          ${source.geo ? `<div><span class="text-muted">Location:</span> <span class="text-text">${source.geo}</span></div>` : ''}
          ${source.hostname ? `<div><span class="text-muted">Hostname:</span> <span class="text-text">${source.hostname}</span></div>` : ''}
          <div class="pt-2">
            ${source.isKnownBad ? '<span class="text-xs px-2 py-1 rounded bg-rose-500/20 text-rose-500">Known Malicious</span>' : ''}
            ${source.isKnownGood ? '<span class="text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-500">Known Good</span>' : ''}
          </div>
        </div>
      </div>

      <!-- Target Info -->
      <div class="rounded-lg border border-border/50 bg-surface/50 p-3">
        <h4 class="text-xs uppercase tracking-wider text-muted mb-2">Target Asset</h4>
        <div class="space-y-1 text-sm">
          <div><span class="text-muted">Host:</span> <span class="font-mono text-text">${target.hostname}</span></div>
          <div><span class="text-muted">Role:</span> <span class="text-text capitalize">${target.role}</span></div>
          <div><span class="text-muted">Criticality:</span> <span class="text-text capitalize">${target.criticality}</span></div>
          ${target.owner ? `<div><span class="text-muted">Owner:</span> <span class="text-text">${target.owner}</span></div>` : ''}
          <div class="pt-2">
            ${target.isPublic ? '<span class="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-500">Public-Facing</span>' : '<span class="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-500">Internal</span>'}
          </div>
        </div>
      </div>

      <!-- Threat Intel -->
      <div class="rounded-lg border border-border/50 bg-surface/50 p-3">
        <h4 class="text-xs uppercase tracking-wider text-muted mb-2">Threat Intel</h4>
        <div class="text-sm">
          ${
            threatIntelNote
              ? `<p class="text-rose-400">${threatIntelNote}</p>`
              : '<p class="text-muted">No threat intelligence matches</p>'
          }
          ${
            ticketReference
              ? `<p class="mt-2 text-emerald-400">Ticket: ${ticketReference}</p>`
              : ''
          }
        </div>
      </div>

      <!-- Baseline / Related -->
      <div class="rounded-lg border border-border/50 bg-surface/50 p-3">
        <h4 class="text-xs uppercase tracking-wider text-muted mb-2">Context</h4>
        <div class="text-sm">
          ${baselineNote ? `<p class="text-muted mb-2">${baselineNote}</p>` : ''}
          ${
            relatedAlerts.length > 0
              ? `
            <p class="text-xs uppercase tracking-wider text-muted mb-1">Related Alerts:</p>
            <ul class="space-y-1">
              ${relatedAlerts
                .map(
                  (ra) => `
                <li class="text-xs">
                  <span class="${SEVERITY_COLORS[ra.severity]}">${ra.severity}</span>
                  <span class="text-text">${ra.title}</span>
                  <span class="text-muted">(${ra.time})</span>
                </li>
              `
                )
                .join('')}
            </ul>
          `
              : '<p class="text-muted">No related alerts in last hour</p>'
          }
        </div>
      </div>
    </div>
  `;
}

function toggleContext() {
  contextExpanded = !contextExpanded;
  contextPanel?.classList.toggle('hidden', !contextExpanded);
  if (contextToggle) {
    contextToggle.textContent = contextExpanded ? 'Hide Context ▲' : 'Show Context ▼';
  }
}

function handleAction(action: Action) {
  if (!gameState || actionsDisabled) return;

  const timeSpent = Date.now() - alertStartTime;
  const alert = getCurrentAlert(gameState);
  if (!alert) return;

  setActionsEnabled(false);
  stopTimer();

  // Submit decision
  gameState = submitDecision(gameState, action, timeSpent);
  const lastResult = gameState.results[gameState.results.length - 1];

  // Show feedback
  showFeedback(lastResult.isCorrect, action, alert.correctAction, alert.explanation);
}

function showFeedback(
  isCorrect: boolean,
  playerAction: Action,
  correctAction: Action,
  explanation: string
) {
  if (!feedbackOverlay || !feedbackIcon || !feedbackTitle || !feedbackText) return;

  feedbackOverlay.classList.remove('hidden');
  setActionsEnabled(false);

  if (isCorrect) {
    feedbackIcon.textContent = '✓';
    feedbackIcon.className = 'text-5xl text-emerald-500';
    feedbackTitle.textContent = 'Correct!';
    feedbackTitle.className = 'text-xl font-medium text-emerald-500';
  } else {
    feedbackIcon.textContent = '✗';
    feedbackIcon.className = 'text-5xl text-rose-500';
    feedbackTitle.textContent = `Should have: ${ACTION_LABELS[correctAction]}`;
    feedbackTitle.className = 'text-xl font-medium text-rose-500';
  }

  feedbackText.innerHTML = `
    <p class="text-sm text-muted mb-2">You chose: <span class="text-text">${ACTION_LABELS[playerAction]}</span></p>
    <p class="text-sm text-text">${explanation}</p>
  `;

  updateScore();
}

function hideFeedback() {
  feedbackOverlay?.classList.add('hidden');

  if (gameState?.isComplete) {
    setActionsEnabled(false);
    endGame();
  } else {
    alertStartTime = Date.now();
    renderCurrentAlert();
    startTimer();
    updateProgress();
  }
}

function updateProgress() {
  if (!gameState || !progressBar || !progressText) return;

  const progress = getProgress(gameState);
  progressBar.style.width = `${progress.percentage}%`;
  progressText.textContent = `${progress.current} / ${progress.total}`;
}

function updateScore() {
  if (!gameState) return;

  if (scoreDisplay) {
    scoreDisplay.textContent = gameState.stats.totalPoints.toString();
  }
  if (streakDisplay) {
    const streak = gameState.stats.streak;
    streakDisplay.textContent = streak > 0 ? `🔥${streak}` : '';
  }
}

function endGame() {
  if (!gameState) return;

  stopTimer();
  showScreen('results');
  renderResults();
}

function renderResults() {
  if (!gameState || !resultsStats || !resultsReview) return;

  const { stats } = gameState;
  const grade = getGrade(stats);

  // Calculate chain statistics
  const chainAlerts = gameState.alerts.filter((a) => a.attackChainId);
  const chainIds = new Set(chainAlerts.map((a) => a.attackChainId));
  const chainCount = chainIds.size;

  // Check how many chain alerts were correctly handled
  const chainResults = gameState.results.filter(
    (_, i) => gameState!.alerts[i].attackChainId
  );
  const chainCorrect = chainResults.filter((r) => r.isCorrect).length;

  resultsStats.innerHTML = `
    <div class="text-center mb-8">
      <div class="text-6xl font-bold ${grade.color} mb-2">${grade.grade}</div>
      <div class="text-xl text-muted">${grade.label}</div>
      <div class="text-3xl font-semibold text-text mt-4">${stats.totalPoints} points</div>
    </div>

    <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
      <div class="rounded-lg border border-border/50 bg-surface/50 p-4 text-center">
        <div class="text-3xl font-semibold text-text">${stats.correctCount}/${stats.totalAlerts}</div>
        <div class="text-sm text-muted">Correct Decisions</div>
      </div>
      <div class="rounded-lg border border-border/50 bg-surface/50 p-4 text-center">
        <div class="text-3xl font-semibold ${stats.precision >= 0.8 ? 'text-emerald-500' : stats.precision >= 0.6 ? 'text-amber-500' : 'text-rose-500'}">
          ${Math.round(stats.precision * 100)}%
        </div>
        <div class="text-sm text-muted">Precision</div>
        <div class="text-xs text-muted mt-1">Of escalations, % that were real</div>
      </div>
      <div class="rounded-lg border border-border/50 bg-surface/50 p-4 text-center">
        <div class="text-3xl font-semibold ${stats.recall >= 0.9 ? 'text-emerald-500' : stats.recall >= 0.7 ? 'text-amber-500' : 'text-rose-500'}">
          ${Math.round(stats.recall * 100)}%
        </div>
        <div class="text-sm text-muted">Recall</div>
        <div class="text-xs text-muted mt-1">Of real attacks, % caught</div>
      </div>
      <div class="rounded-lg border border-border/50 bg-surface/50 p-4 text-center">
        <div class="text-3xl font-semibold text-text">${(stats.avgTimeMs / 1000).toFixed(1)}s</div>
        <div class="text-sm text-muted">Avg Decision Time</div>
      </div>
    </div>

    <div class="grid gap-4 ${chainCount > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'} mb-8">
      <div class="rounded-lg border ${stats.missedAttacks === 0 ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-rose-500/30 bg-rose-500/10'} p-4 text-center">
        <div class="text-2xl font-semibold ${stats.missedAttacks === 0 ? 'text-emerald-500' : 'text-rose-500'}">${stats.missedAttacks}</div>
        <div class="text-sm text-muted">Missed Attacks</div>
      </div>
      <div class="rounded-lg border ${stats.falseEscalations <= 1 ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-amber-500/30 bg-amber-500/10'} p-4 text-center">
        <div class="text-2xl font-semibold ${stats.falseEscalations <= 1 ? 'text-emerald-500' : 'text-amber-500'}">${stats.falseEscalations}</div>
        <div class="text-sm text-muted">False Escalations</div>
      </div>
      <div class="rounded-lg border border-accent/30 bg-accent/10 p-4 text-center">
        <div class="text-2xl font-semibold text-accent">${stats.maxStreak}</div>
        <div class="text-sm text-muted">Best Streak</div>
      </div>
      ${
        chainCount > 0
          ? `
        <div class="rounded-lg border ${chainCorrect === chainAlerts.length ? 'border-purple-500/30 bg-purple-500/10' : 'border-purple-500/20 bg-purple-500/5'} p-4 text-center">
          <div class="text-2xl font-semibold ${chainCorrect === chainAlerts.length ? 'text-purple-400' : 'text-purple-300'}">${chainCorrect}/${chainAlerts.length}</div>
          <div class="text-sm text-muted">Chain Alerts</div>
          <div class="text-xs text-muted mt-1">${chainCount} attack chain${chainCount > 1 ? 's' : ''}</div>
        </div>
      `
          : ''
      }
    </div>
  `;

  // Render review of each alert
  resultsReview.innerHTML = `
    <h3 class="text-lg font-medium text-text mb-4">Alert Review</h3>
    <div class="space-y-3">
      ${gameState.results
        .map((result, i) => {
          const alert = gameState!.alerts[i];
          const isCorrect = result.isCorrect;
          const chainBadge = alert.attackChainId
            ? `<span class="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 ml-2">Chain ${alert.attackChainStep}/${alert.attackChainTotal}</span>`
            : '';
          return `
          <details class="rounded-lg border ${isCorrect ? 'border-emerald-500/30' : 'border-rose-500/30'} ${alert.attackChainId ? 'ring-1 ring-purple-500/30' : ''} bg-surface/50">
            <summary class="p-3 cursor-pointer flex items-center justify-between">
              <div class="flex items-center gap-3">
                <span class="${isCorrect ? 'text-emerald-500' : 'text-rose-500'}">${isCorrect ? '✓' : '✗'}</span>
                <span class="text-sm text-text">${alert.title}</span>
                ${chainBadge}
              </div>
              <div class="flex items-center gap-3 text-sm">
                <span class="text-muted">You: ${ACTION_LABELS[result.playerAction]}</span>
                ${!isCorrect ? `<span class="text-rose-400">→ ${ACTION_LABELS[result.correctAction]}</span>` : ''}
                <span class="${result.points >= 0 ? 'text-emerald-500' : 'text-rose-500'}">${result.points > 0 ? '+' : ''}${result.points}</span>
              </div>
            </summary>
            <div class="p-3 pt-0 border-t border-border/30 mt-2">
              <p class="text-sm text-muted">${result.explanation}</p>
            </div>
          </details>
        `;
        })
        .join('')}
    </div>
  `;
}

function resetGame() {
  gameState = null;
  showScreen('start');
}

// Event listeners
difficultyButtons.forEach((btn) => {
  btn.addEventListener('click', () => {
    const difficulty = btn.dataset.difficulty as Difficulty;
    if (difficulty && DIFFICULTY_CONFIG[difficulty]) {
      startGame(difficulty);
    }
  });
});

actionButtons.forEach((btn) => {
  btn.addEventListener('click', () => {
    const action = btn.dataset.action as Action;
    if (action) {
      handleAction(action);
    }
  });
});

contextToggle?.addEventListener('click', toggleContext);
feedbackContinue?.addEventListener('click', hideFeedback);
playAgainBtn?.addEventListener('click', resetGame);

// Keyboard shortcuts
document.addEventListener('keydown', (e) => {
  if (!gameState || gameState.isComplete) return;
  const key = e.key.toLowerCase();
  if (feedbackOverlay && !feedbackOverlay.classList.contains('hidden')) {
    if (key === 'enter' || e.code === 'Space') {
      e.preventDefault();
      hideFeedback();
    }
    return;
  }

  switch (key) {
    case '1':
    case 'e':
      handleAction('escalate');
      break;
    case '2':
    case 'i':
      handleAction('investigate');
      break;
    case '3':
    case 'd':
      handleAction('dismiss');
      break;
    case '4':
    case 't':
      handleAction('tune');
      break;
    case 'c':
      toggleContext();
      break;
  }
});

// Initialize
showScreen('start');
